/**
 * Компонент галереи изображений
 */

import { useState, useEffect } from 'react';
import { useImageProxy } from '@/hooks/useImageProxy';
import styles from './ImageGallery.module.css';

/**
 * Элемент галереи
 */
export interface GalleryImage {
  /** URL изображения */
  url: string;
  /** Подпись к изображению */
  caption?: string;
}

/**
 * Пропсы компонента ImageGallery
 */
interface ImageGalleryProps {
  /** Массив изображений */
  images: GalleryImage[];
}

/**
 * Компонент одного изображения в галерее
 */
function GalleryItem({ image, onClick }: { image: GalleryImage; onClick: () => void }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const { url: blobUrl, status } = useImageProxy(image.url);

  if (status === 'loading' || !blobUrl) {
    return (
      <div className={styles.item}>
        <div className={styles.skeleton} />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={styles.item}>
        <div className={styles.errorPlaceholder}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={styles.item} 
      onClick={onClick} 
      role="button" 
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {!isLoaded && <div className={styles.skeleton} />}
      <img 
        src={blobUrl} 
        alt={image.caption || 'Изображение'} 
        className={`${styles.image} ${isLoaded ? styles.loaded : ''}`}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
        onError={() => setIsLoaded(true)}
      />
      {image.caption && (
        <div className={styles.caption}>{image.caption}</div>
      )}
      <div className={styles.overlay}>
        <span>Нажмите для увеличения</span>
      </div>
    </div>
  );
}

/**
 * Компонент одного изображения для Lightbox
 */
function LightboxImage({ url }: { url: string }) {
  const { url: blobUrl } = useImageProxy(url);
  
  if (!blobUrl) {
    return null;
  }
  
  return <img src={blobUrl} alt="Изображение" style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />;
}

/**
 * Галерея изображений с подписями
 * 
 * @description Отображает сетку изображений с подписями.
 * Клик на изображение открывает Lightbox с возможностью навигации.
 */
export function ImageGallery({ images }: ImageGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const handleOpenLightbox = (index: number) => {
    setLightboxIndex(index);
  };

  const handleCloseLightbox = () => {
    setLightboxIndex(null);
  };

  const handlePrevious = () => {
    if (lightboxIndex !== null && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  };

  const handleNext = () => {
    if (lightboxIndex !== null && lightboxIndex < images.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseLightbox();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex]);

  return (
    <>
      <div className={styles.gallery}>
        {images.map((image, index) => (
          <GalleryItem
            key={`${image.url}-${index}`}
            image={image}
            onClick={() => handleOpenLightbox(index)}
          />
        ))}
      </div>

      {/* Lightbox с навигацией - НЕ используем стандартный Lightbox с imageUrl */}
      {lightboxIndex !== null && (
        <div className={styles.lightboxCustom}>
          <div className={styles.lightboxOverlay} onClick={handleCloseLightbox}>
            <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
              <LightboxImage url={images[lightboxIndex].url} />
              
              {/* Заголовок/подпись */}
              {images[lightboxIndex].caption && (
                <div className={styles.lightboxCaption}>{images[lightboxIndex].caption}</div>
              )}
              
              {/* Кнопка закрыть */}
              <button className={styles.lightboxClose} onClick={handleCloseLightbox} aria-label="Закрыть">
                ×
              </button>
              
              {/* Навигация */}
              {lightboxIndex > 0 && (
                <button className={styles.lightboxPrev} onClick={handlePrevious} aria-label="Предыдущее">
                  ‹
                </button>
              )}
              
              {lightboxIndex < images.length - 1 && (
                <button className={styles.lightboxNext} onClick={handleNext} aria-label="Следующее">
                  ›
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

